import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { commissionApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Wallet,
  FileText,
  CheckCircle2,
  Clock,
  Receipt,
  CreditCard,
  ChevronRight,
  Filter,
  Euro,
  TrendingUp,
  Users
} from 'lucide-react';

// Status configuration
const statusConfig = {
  maturata: { 
    label: 'Maturata', 
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: Clock,
    description: 'In attesa validazione Admin'
  },
  da_fatturare: { 
    label: 'Da Fatturare', 
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    icon: FileText,
    description: 'Pronta per fatturazione'
  },
  fatturata: { 
    label: 'Fatturata', 
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: Receipt,
    description: 'In attesa pagamento'
  },
  pagata: { 
    label: 'Pagata', 
    color: 'bg-green-100 text-green-700 border-green-200',
    icon: CheckCircle2,
    description: 'Completata'
  },
};

const CommissionsPage = () => {
  const { user, isAdmin, isAdministration, isTechnician } = useAuth();
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedTechnician, setSelectedTechnician] = useState('all');
  const [actionDialog, setActionDialog] = useState({ open: false, commission: null, action: null });
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedStatus, selectedTechnician]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedStatus !== 'all') params.status = selectedStatus;
      if (selectedTechnician !== 'all') params.technician_id = selectedTechnician;

      const [commissionsRes, summaryRes] = await Promise.all([
        commissionApi.getAll(params),
        commissionApi.getSummary()
      ]);

      setCommissions(commissionsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading commissions:', error);
      toast.error('Errore nel caricamento delle provvigioni');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!actionDialog.commission || !actionDialog.action) return;
    
    setProcessing(true);
    try {
      const { commission, action } = actionDialog;
      
      if (action === 'fatturata' && !invoiceNumber) {
        toast.error('Inserisci il numero fattura');
        setProcessing(false);
        return;
      }

      await commissionApi.updateStatus(
        commission.id,
        action,
        action === 'fatturata' ? invoiceNumber : null,
        action === 'fatturata' ? invoiceDate || null : null,
        null
      );

      toast.success('Stato aggiornato con successo');
      setActionDialog({ open: false, commission: null, action: null });
      setInvoiceNumber('');
      setInvoiceDate('');
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    } finally {
      setProcessing(false);
    }
  };

  const openActionDialog = (commission, action) => {
    setActionDialog({ open: true, commission, action });
    setInvoiceNumber('');
    setInvoiceDate('');
  };

  // Get unique technicians for filter (admin only)
  const technicians = summary?.by_technician || [];

  if (loading && !commissions.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" />
      </div>
    );
  }

  // Render Technician View
  if (isTechnician) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="commissions-page">
        <div>
          <h1 className="text-h2">Le Mie Provvigioni</h1>
          <p className="text-body-small mt-1">Gestisci le tue provvigioni e fatture</p>
        </div>

        {/* Summary Cards - Clickable filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card 
            className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'maturata' ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => setSelectedStatus(selectedStatus === 'maturata' ? 'all' : 'maturata')}
            data-testid="filter-maturate"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-[#737373]">Maturate</p>
                  <p className="text-lg font-semibold">{formatCurrency(summary?.maturate || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'da_fatturare' ? 'ring-2 ring-amber-500' : ''}`}
            onClick={() => setSelectedStatus(selectedStatus === 'da_fatturare' ? 'all' : 'da_fatturare')}
            data-testid="filter-da-fatturare"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-[#737373]">Da Fatturare</p>
                  <p className="text-lg font-semibold">{formatCurrency(summary?.da_fatturare || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'fatturata' ? 'ring-2 ring-purple-500' : ''}`}
            onClick={() => setSelectedStatus(selectedStatus === 'fatturata' ? 'all' : 'fatturata')}
            data-testid="filter-fatturate"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Receipt className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-[#737373]">Fatturate</p>
                  <p className="text-lg font-semibold">{formatCurrency(summary?.fatturate || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'pagata' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => setSelectedStatus(selectedStatus === 'pagata' ? 'all' : 'pagata')}
            data-testid="filter-pagate"
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-[#737373]">Pagate</p>
                  <p className="text-lg font-semibold">{formatCurrency(summary?.pagate || 0)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Total Earned Card */}
        <Card className="card-abitalo bg-gradient-to-r from-[#FF7819] to-[#FF9A4D] text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">Totale Provvigioni</p>
                <p className="text-3xl font-bold mt-1">{formatCurrency(summary?.totale || 0)}</p>
                <p className="text-white/80 text-sm mt-2">{summary?.count || 0} contratti</p>
              </div>
              <Wallet className="w-16 h-16 text-white/30" />
            </div>
          </CardContent>
        </Card>

        {/* Filter Tabs */}
        <Tabs value={selectedStatus} onValueChange={setSelectedStatus}>
          <TabsList className="grid w-full grid-cols-5 h-auto">
            <TabsTrigger value="all" className="text-xs py-2">Tutte</TabsTrigger>
            <TabsTrigger value="maturata" className="text-xs py-2">Maturate</TabsTrigger>
            <TabsTrigger value="da_fatturare" className="text-xs py-2">Da Fatturare</TabsTrigger>
            <TabsTrigger value="fatturata" className="text-xs py-2">Fatturate</TabsTrigger>
            <TabsTrigger value="pagata" className="text-xs py-2">Pagate</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Commissions List */}
        <div className="space-y-3">
          {commissions.length === 0 ? (
            <Card className="card-abitalo">
              <CardContent className="p-8 text-center">
                <Wallet className="w-12 h-12 text-[#DCDCDC] mx-auto mb-4" />
                <p className="text-[#737373]">Nessuna provvigione trovata</p>
              </CardContent>
            </Card>
          ) : (
            commissions.map((commission) => {
              const config = statusConfig[commission.status] || statusConfig.maturata;
              const StatusIcon = config.icon;
              
              return (
                <Card key={commission.id} className="card-abitalo hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={`${config.color} border`}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {config.label}
                          </Badge>
                          <span className="text-xs text-[#737373]">
                            {formatDate(commission.created_at)}
                          </span>
                        </div>
                        <p className="font-medium text-[#171717]">{commission.contract_number}</p>
                        <p className="text-sm text-[#737373]">{commission.client_name}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="text-[#737373]">
                            Base: {formatCurrency(commission.contract_amount)}
                          </span>
                          <span className="text-[#737373]">
                            {commission.percentage}%
                          </span>
                        </div>
                        {commission.invoice_number && (
                          <p className="text-xs text-[#737373] mt-1">
                            Fattura: {commission.invoice_number}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-[#FF7819]">
                          {formatCurrency(commission.commission_amount)}
                        </p>
                        {commission.status === 'da_fatturare' && (
                          <Button 
                            size="sm" 
                            className="mt-2 btn-primary"
                            onClick={() => openActionDialog(commission, 'fatturata')}
                            data-testid={`emit-invoice-${commission.id}`}
                          >
                            <Receipt className="w-4 h-4 mr-1" />
                            Emetti Fattura
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Invoice Dialog */}
        <Dialog open={actionDialog.open && actionDialog.action === 'fatturata'} onOpenChange={(open) => !open && setActionDialog({ open: false, commission: null, action: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registra Fattura Emessa</DialogTitle>
              <DialogDescription>
                Inserisci i dati della fattura per la provvigione di {formatCurrency(actionDialog.commission?.commission_amount || 0)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="invoice_number">Numero Fattura *</Label>
                <Input
                  id="invoice_number"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Es: FT-2025/001"
                  data-testid="invoice-number-input"
                />
              </div>
              <div>
                <Label htmlFor="invoice_date">Data Fattura</Label>
                <Input
                  id="invoice_date"
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  data-testid="invoice-date-input"
                />
              </div>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="outline" onClick={() => setActionDialog({ open: false, commission: null, action: null })}>
                Annulla
              </Button>
              <Button className="btn-primary" onClick={handleStatusUpdate} disabled={processing}>
                {processing ? 'Salvataggio...' : 'Conferma Fattura'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Render Admin/Administration View
  return (
    <div className="space-y-6 animate-fade-in" data-testid="commissions-page">
      <div>
        <h1 className="text-h2">Gestione Provvigioni</h1>
        <p className="text-body-small mt-1">Monitora e gestisci le provvigioni dei tecnici esterni</p>
      </div>

      {/* Global Summary Cards - Clickable filters */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'maturata' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setSelectedStatus(selectedStatus === 'maturata' ? 'all' : 'maturata')}
          data-testid="admin-filter-maturate"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-[#737373]">Da Validare</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.global?.maturate || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'da_fatturare' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setSelectedStatus(selectedStatus === 'da_fatturare' ? 'all' : 'da_fatturare')}
          data-testid="admin-filter-da-fatturare"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-[#737373]">In Fatturazione</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.global?.da_fatturare || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'fatturata' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setSelectedStatus(selectedStatus === 'fatturata' ? 'all' : 'fatturata')}
          data-testid="admin-filter-fatturate"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs text-[#737373]">Da Pagare</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.global?.fatturate || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`card-abitalo cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${selectedStatus === 'pagata' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setSelectedStatus(selectedStatus === 'pagata' ? 'all' : 'pagata')}
          data-testid="admin-filter-pagate"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-[#737373]">Totale Pagato</p>
                <p className="text-lg font-semibold">{formatCurrency(summary?.global?.pagate || 0)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Technicians Summary */}
      {technicians.length > 0 && (
        <Card className="card-abitalo">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-[#FF7819]" />
              Riepilogo per Tecnico
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-[#F2F2F2]">
              {technicians.map((tech) => (
                <div 
                  key={tech.technician_id} 
                  className="py-3 flex items-center justify-between cursor-pointer hover:bg-[#FAFAFA] -mx-4 px-4 transition-colors"
                  onClick={() => setSelectedTechnician(tech.technician_id)}
                >
                  <div>
                    <p className="font-medium text-[#171717]">{tech.technician_name}</p>
                    <div className="flex gap-3 mt-1 text-xs">
                      {tech.by_status.maturata && (
                        <span className="text-blue-600">
                          {tech.by_status.maturata.count} maturate
                        </span>
                      )}
                      {tech.by_status.da_fatturare && (
                        <span className="text-amber-600">
                          {tech.by_status.da_fatturare.count} da fatturare
                        </span>
                      )}
                      {tech.by_status.fatturata && (
                        <span className="text-purple-600">
                          {tech.by_status.fatturata.count} fatturate
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#FF7819]">
                      {formatCurrency(tech.total_amount)}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#DCDCDC]" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#737373]" />
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger className="w-[180px]" data-testid="status-filter">
              <SelectValue placeholder="Filtra per stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="maturata">Maturate</SelectItem>
              <SelectItem value="da_fatturare">Da Fatturare</SelectItem>
              <SelectItem value="fatturata">Fatturate</SelectItem>
              <SelectItem value="pagata">Pagate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {technicians.length > 0 && (
          <Select value={selectedTechnician} onValueChange={setSelectedTechnician}>
            <SelectTrigger className="w-[200px]" data-testid="technician-filter">
              <SelectValue placeholder="Filtra per tecnico" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tecnici</SelectItem>
              {technicians.map((tech) => (
                <SelectItem key={tech.technician_id} value={tech.technician_id}>
                  {tech.technician_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Commissions List */}
      <div className="space-y-3">
        {commissions.length === 0 ? (
          <Card className="card-abitalo">
            <CardContent className="p-8 text-center">
              <Wallet className="w-12 h-12 text-[#DCDCDC] mx-auto mb-4" />
              <p className="text-[#737373]">Nessuna provvigione trovata</p>
            </CardContent>
          </Card>
        ) : (
          commissions.map((commission) => {
            const config = statusConfig[commission.status] || statusConfig.maturata;
            const StatusIcon = config.icon;

            return (
              <Card key={commission.id} className="card-abitalo hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge className={`${config.color} border`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {config.label}
                        </Badge>
                        <span className="text-xs text-[#737373]">
                          {formatDate(commission.created_at)}
                        </span>
                      </div>
                      <p className="font-medium text-[#171717]">{commission.technician_name}</p>
                      <p className="text-sm text-[#737373]">
                        {commission.contract_number} - {commission.client_name}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <span className="text-[#737373]">
                          Contratto: {formatCurrency(commission.contract_amount)}
                        </span>
                        <span className="text-[#737373]">
                          {commission.percentage}%
                        </span>
                      </div>
                      {commission.invoice_number && (
                        <p className="text-xs text-[#737373] mt-1">
                          Fattura: {commission.invoice_number} 
                          {commission.invoice_date && ` del ${formatDate(commission.invoice_date)}`}
                        </p>
                      )}
                    </div>
                    <div className="text-right flex flex-col items-end gap-2">
                      <p className="text-xl font-bold text-[#FF7819]">
                        {formatCurrency(commission.commission_amount)}
                      </p>
                      {commission.status === 'maturata' && (
                        <Button 
                          size="sm" 
                          className="btn-primary"
                          onClick={() => openActionDialog(commission, 'da_fatturare')}
                          data-testid={`validate-${commission.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Valida
                        </Button>
                      )}
                      {commission.status === 'fatturata' && (
                        <Button 
                          size="sm" 
                          className="btn-primary"
                          onClick={() => openActionDialog(commission, 'pagata')}
                          data-testid={`pay-${commission.id}`}
                        >
                          <CreditCard className="w-4 h-4 mr-1" />
                          Segna Pagata
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Validate Dialog */}
      <Dialog open={actionDialog.open && actionDialog.action === 'da_fatturare'} onOpenChange={(open) => !open && setActionDialog({ open: false, commission: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Valida Provvigione</DialogTitle>
            <DialogDescription>
              Confermi di voler validare la provvigione di {formatCurrency(actionDialog.commission?.commission_amount || 0)} per {actionDialog.commission?.technician_name}?
              <br /><br />
              Il tecnico riceverà una notifica e potrà emettere fattura.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setActionDialog({ open: false, commission: null, action: null })}>
              Annulla
            </Button>
            <Button className="btn-primary" onClick={handleStatusUpdate} disabled={processing}>
              {processing ? 'Validazione...' : 'Conferma Validazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay Dialog */}
      <Dialog open={actionDialog.open && actionDialog.action === 'pagata'} onOpenChange={(open) => !open && setActionDialog({ open: false, commission: null, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Pagamento</DialogTitle>
            <DialogDescription>
              Confermi di aver pagato la provvigione di {formatCurrency(actionDialog.commission?.commission_amount || 0)} a {actionDialog.commission?.technician_name}?
              <br /><br />
              Fattura: {actionDialog.commission?.invoice_number}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setActionDialog({ open: false, commission: null, action: null })}>
              Annulla
            </Button>
            <Button className="btn-primary" onClick={handleStatusUpdate} disabled={processing}>
              {processing ? 'Conferma...' : 'Conferma Pagamento'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommissionsPage;
